import React, { RefObject } from 'react';
import { styled } from '@mui/material/styles';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { AnswerType } from 'reducers/answers';
import { selectLegibleElementColor } from 'reducers/config';
import { navigateTo } from 'actions/main';
import {
  sendTrackingEvent,
  setSelectedArticle,
} from 'actions/workflows/workflows';
import { ARTICLE_PAGE } from 'constants/pageConsts';
import { selectCustomConfigParams } from 'reducers/workflows';

import { useTheme } from '@mui/material/styles';
import { useBorderColor } from 'components/App/utils';
import { ArticleStepData, ReadArticleActions } from 'api/types';
import sanitizeHtml from 'utils/sanitizeHtml';

interface ArticleProps {
  /** Individual article  */
  article: AnswerType;
  /** Index of the article in the article array */
  articleIndex: number;
  /** Article step data used in tracking event */
  articleStepData?: ArticleStepData;
  /** Boolean to determine if the article is selected */
  isSelected: boolean;
  /** How to open article to read */
  readArticleAction?: ReadArticleActions;
  /** Text used for link to open / read article */
  readArticleText: string;
  /** React Ref object used to determine scroll position on modal */
  reference?: RefObject<HTMLDivElement> | null;
  /** Callback function to set a selected article */
  selectArticle: () => void;
  /** Boolean to determine if article needs a colored border */
  setBorder: boolean;
}

export const appendAnalyticsParamsToURL = (url: string): string => {
  const urlObject = new URL(url);
  // UTM stands for Urchin Tracking Module and is used for analytics tracking,
  // see https://en.wikipedia.org/wiki/UTM_parameters
  urlObject.searchParams.append('utm_source', 'forethought-chat');
  urlObject.searchParams.append('utm_lp', 'forethought-chat');
  return urlObject.href;
};

const Article: React.FC<React.PropsWithChildren<ArticleProps>> = ({
  article,
  articleIndex,
  articleStepData,
  isSelected,
  readArticleAction = 'new_tab',
  readArticleText,
  reference,
  selectArticle,
  setBorder,
}) => {
  const {
    document_id: documentId,
    link,
    preview,
    source_type: sourceType,
    span_id: spanId,
    title,
    trace_id: traceId,
  } = article;
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const legibleElementColor = useAppSelector(selectLegibleElementColor);
  const customConfigParams = useAppSelector(selectCustomConfigParams);
  const borderColor = useBorderColor();

  const normalizedReadArticleAction =
    readArticleAction === 'new_tab' && !link
      ? 'expand_in_widget'
      : readArticleAction;
  const hideNav = customConfigParams?.hide_nav ?? '';
  const hasArticleLink = sourceType === 'knowledge' && !!link;
  const hasFullHtml = !!article.full_html;
  const openInWidget =
    hideNav !== 'true' &&
    normalizedReadArticleAction === 'expand_in_widget' &&
    hasFullHtml;
  const readArticleAriaLabel = `${readArticleText}: ${title}`;

  const sendArticleTrackingEvent = () => {
    dispatch(
      sendTrackingEvent({
        current_step_id: articleStepData?.current_step_id,
        doc_id: article.document_id,
        doc_index: articleIndex,
        event_type: 'go-to-article',
        intent_workflow_id: articleStepData?.intent_workflow_id,
        intent_workflow_version: articleStepData?.intent_workflow_version,
        org_id: articleStepData?.org_id,
        span_id: spanId,
        standardized_document_id: article.standardized_document_id,
        trace_id: traceId,
      }),
    );
  };

  const openArticleInWidget = () => {
    if (openInWidget) {
      dispatch(setSelectedArticle(article));
      dispatch(navigateTo(ARTICLE_PAGE));
    }
    sendArticleTrackingEvent();
  };

  const renderReadArticleLink = () => {
    if (normalizedReadArticleAction === 'none') {
      return null;
    }

    if (openInWidget) {
      return (
        <ReadArticleLink
          aria-label={readArticleAriaLabel}
          as='button'
          data-testid={`read-article-link-${articleIndex}`}
          onClick={openArticleInWidget}
          themeColor={legibleElementColor}
        >
          {readArticleText}
        </ReadArticleLink>
      );
    }

    if (hasArticleLink) {
      return (
        <ReadArticleLink
          aria-label={readArticleAriaLabel}
          data-testid={`read-article-link-${articleIndex}`}
          href={appendAnalyticsParamsToURL(link)}
          onClick={sendArticleTrackingEvent}
          rel='noopener'
          target='_blank'
          themeColor={legibleElementColor}
        >
          {readArticleText}
        </ReadArticleLink>
      );
    }

    return null;
  };

  const renderedTitle = (
    <ArticleTitle
      color={theme.palette.text.primary}
      // eslint-disable-next-line react/forbid-component-props
      dangerouslySetInnerHTML={{ __html: sanitizeHtml(title) }}
    />
  );

  const closedArticleTitle = (
    <ClosedArticleTitle
      // eslint-disable-next-line react/forbid-component-props
      dangerouslySetInnerHTML={{ __html: sanitizeHtml(title) }}
    />
  );

  if (!isSelected) {
    return (
      <ClosedArticleContainer
        backgroundColor={theme.palette.colors.white}
        borderColor={borderColor}
        data-testid={`closed-article-${articleIndex}`}
        onClick={() => selectArticle()}
        themeColor={legibleElementColor}
        type='button'
      >
        {closedArticleTitle}
      </ClosedArticleContainer>
    );
  } else {
    return (
      <ArticleContainer
        backgroundColor={theme.palette.colors.white}
        borderColor={borderColor}
        data-testid={`article-${articleIndex}`}
        key={documentId}
        ref={reference}
        setBorder={setBorder}
        tabIndex={0}
        themeColor={legibleElementColor}
      >
        {renderedTitle}
        <AnswerContentBody
          borderColor={theme.palette.colors.white}
          // eslint-disable-next-line react/forbid-component-props
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(preview) }}
          focusColor={legibleElementColor}
          tabIndex={0}
        />
        {renderReadArticleLink()}
      </ArticleContainer>
    );
  }
};

export default Article;

const ArticleContainer = styled('article')<{
  backgroundColor: string;
  borderColor: string;
  setBorder: boolean;
  themeColor: string;
}>`
  word-break: break-word;
  background-color: ${props => props.backgroundColor};
  line-height: 1.2rem;
  border: ${props =>
    props.setBorder
      ? `1px solid ${props.themeColor}`
      : `1px solid ${props.borderColor}`};

  border-radius: 5px;
  margin-bottom: 15px;
  padding: 7%;
  border-radius: 20px;
  &:focus {
    outline: none;
  }
  &:focus-visible {
    border: 2px solid ${props => props.themeColor};
  }
`;

const ClosedArticleContainer = styled('button')<{
  backgroundColor: string;
  borderColor: string;
  themeColor: string;
}>`
  width: 100%;
  text-align: left;
  background-color: ${props => props.backgroundColor};
  border: 1px solid ${props => props.borderColor};
  border-radius: 5px;
  padding: 7%;
  border-radius: 20px;
  margin-bottom: 15px;
  cursor: pointer;
  &:focus {
    outline: none;
  }
  &:focus-visible {
    border: 2px solid ${props => props.themeColor};
  }
`;

const ClosedArticleTitle = styled('span')`
  margin: 0 0 10px;
`;

const ArticleTitle = styled('h1')<{
  color: string;
}>`
  font-size: 16px;
  font-weight: 600;
  line-height: 22px;
  margin: 0;
  color: ${props => props.color};
`;

const AnswerContentBody = styled('div')<{
  borderColor: string;
  focusColor: string;
}>`
  margin-bottom: 10px;
  border: 1px solid ${props => props.borderColor};
  max-height: 200px;
  overflow: hidden;
  position: relative;
  font-size: 14px;
  line-height: 20px;
  &::after {
    content: '';
    text-align: right;
    position: absolute;
    bottom: 0;
    right: 0;
    width: 100%;
    height: 20px;
    background: linear-gradient(
      rgba(255, 255, 255, 0),
      rgba(255, 255, 255, 1) 70%
    );
  }

  & h2 {
    font-size: 16px;
  }

  & h3,
  h4,
  h5 {
    font-size: 15px;
  }

  & pre {
    overflow-x: auto;
    white-space: pre-wrap;
    word-break: break-word;
  }

  & .article_header {
    display: none;
  }

  & a {
    color: var(--message-link-color);
    word-break: break-word;
  }

  & a:visited {
    color: ${props => props.theme.palette.colors.purple[600]};
  }

  & a:hover {
    color: ${props => props.theme.palette.colors.purple[600]};
    text-decoration: none;
  }

  & a:active {
    color: ${props => props.theme.palette.colors.purple[700]};
  }

  & img {
    max-width: 100% !important;
    height: auto !important;
    width: initial !important;
  }

  & dl,
  dd,
  dt {
    margin: 0;
  }

  & iframe,
  video,
  dl,
  dd,
  dt {
    width: 100%;
    height: auto;
  }

  & table {
    width: 100% !important;
    table-layout: auto;
  }
  & td {
    padding: 3px;
    word-break: break-word;
    height: auto;
  }

  & ft-highlight {
    background: #fbf2de;
    color: #404b5a;
    font-weight: 600;
    text-decoration: underline;
    text-decoration-color: #edbd59;
    text-decoration-style: solid;
    text-underline-position: under;
  }

  &:focus {
    outline: none;
  }
  &:focus-visible {
    border: 1px solid var(--message-link-color);
  }
`;

export const ReadArticleLink = styled('a')<{ themeColor: string }>`
  color: ${props => props.themeColor};
  font-size: 16px;
  display: block;
  text-align: initial;
  padding: 0 14px 20px 0;
  text-decoration: underline;
  cursor: pointer;
  padding-bottom: 0;
  margin-top: 20px;
  background-color: transparent;
  border: none;
`;
